import { useState, type ChangeEvent } from 'react';
import { rechazoDeArchivos } from '../lib/archivoDeFoto';

/**
 * Una pregunta de foto con DOS caminos: tomarla ahora o elegirla de la galería del celular.
 *
 * Pedido del usuario (2026-08-17): *"cuando hayan preguntas para subir fotos también tenga la
 * opción para subir fotos de la memoria del celular"*.
 *
 * **Por qué dos `input` y no uno sin `capture`:** un input sin `capture` también abre el selector
 * nativo con las dos opciones, pero le agrega un toque al camino de la cámara — que es el que se
 * usa de pie frente al cliente y estaba optimizado a propósito. Con dos botones, tomar la foto
 * sigue siendo un toque y la galería aparece al lado.
 *
 * **Por qué la galería importa en esta operación:** en las tiendas que no dejan usar el celular en
 * sala, la impulsadora fotografía la boleta al salir y registra el canje después. Sin este camino
 * esa foto existía en el celular y no había forma de subirla.
 *
 * ⚠️ **NO usar esto para la foto del MARCADO de asistencia.** Ahí la foto no es un dato, es la
 * prueba de que la persona estaba en la tienda a esa hora: va atada al GPS y al sello. Permitir
 * elegirla de la galería dejaría marcar asistencia desde la casa con una foto de otro día, que es
 * exactamente lo que la geocerca existe para impedir. Ese input se queda solo con `capture`.
 */
export default function CampoFoto({
  etiqueta, nombre, listaTexto, lista, onElegir, disabled, varias,
}: {
  /** Opcional: en el formulario del marcado la etiqueta del campo ya se pinta aparte. */
  etiqueta?: string;
  /**
   * Deja elegir VARIAS fotos de una vez (2026-08-19, pedido del usuario para el levantamiento:
   * *"en todos los campos donde se puede subir fotos, se puedan subir más de una"*).
   *
   * Solo cambia de verdad el camino de la galería, y eso está bien: el selector nativo permite
   * marcar varias, mientras que la cámara devuelve una por disparo en la práctica totalidad de los
   * celulares. O sea que con `varias` la galería se vuelve el camino rápido para cargar las cinco
   * fotos de un puesto, y la cámara sigue sirviendo para ir sumándolas de a una.
   *
   * Por defecto está APAGADO: quien lo necesite lo pide. Encenderlo para todos convertiría en lista
   * campos que hoy guardan una sola foto —la de la boleta de un canje, por ejemplo— y el que
   * eligiera dos vería desaparecer la primera sin ningún aviso.
   */
  varias?: boolean;
  /**
   * El sujeto de la foto, en minúscula y con artículo: "la boleta", "el premio".
   *
   * No es decorativo: los dos botones dicen lo mismo en toda la pantalla («Tomar foto», «De la
   * galería»), así que sin esto los nombres accesibles quedan repetidos y quien usa lector de
   * pantalla no sabe cuál de las dos fotos está subiendo.
   */
  nombre: string;
  listaTexto: string;
  lista: boolean;
  /**
   * Recibe el evento entero, no un `File`, justamente para que con `varias` el llamador pueda
   * leer `e.target.files` completo. Devolver solo el primero acá dejaría al componente decidiendo
   * en silencio que las demás fotos no importan.
   */
  onElegir: (e: ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
}) {
  const [rechazo, setRechazo] = useState<string | null>(null);
  /**
   * Lo que no se puede subir se rechaza ACÁ, antes de llegar a quien llama (tipo y tope del
   * bucket, `lib/archivoDeFoto`). Va en el componente y no en cada pantalla porque esta es la
   * pregunta de foto de seis formularios: con el control en cada uno, el séptimo nacería sin él.
   *
   * En estos formularios la foto puede quedar en la cola sin señal y subirse horas después: si el
   * servidor la rechazara recién ahí, el canje o la visita quedarían «fallidos» con la persona ya
   * fuera del puesto. Se mide el original porque, si el navegador no logra recomprimirla, viaja tal
   * cual.
   */
  function alElegir(e: ChangeEvent<HTMLInputElement>) {
    const motivo = rechazoDeArchivos(Array.from(e.target.files ?? []));
    if (motivo !== null) {
      setRechazo(motivo);
      // Vaciado para que volver a elegir otra (o la misma, ya editada) dispare `change` otra vez.
      e.target.value = '';
      return;
    }
    setRechazo(null);
    onElegir(e);
  }
  return (
    <div className="block">
      {etiqueta !== undefined && <span className="text-sm text-gray-700">{etiqueta}</span>}
      <div className={`flex gap-2 ${etiqueta !== undefined ? 'mt-1' : ''}`}>
        <label
          className={`min-h-[44px] flex-1 rounded border border-blue-300 bg-blue-50 px-3 py-3 text-center text-sm font-medium text-blue-800 ${
            disabled === true ? 'opacity-50' : 'cursor-pointer'
          }`}
        >
          Tomar foto
          <input
            type="file"
            accept="image/*"
            capture="environment"
            multiple={varias}
            disabled={disabled}
            onChange={alElegir}
            // La cámara conserva la etiqueta de la pregunta: es el camino principal y el que la
            // etiqueta describe. Así "Foto de la boleta" sigue nombrando al input que la toma.
            aria-label={etiqueta ?? `Tomar foto: ${nombre}`}
            className="hidden"
          />
        </label>
        <label
          className={`min-h-[44px] flex-1 rounded border px-3 py-3 text-center text-sm text-gray-700 ${
            disabled === true ? 'opacity-50' : 'cursor-pointer'
          }`}
        >
          De la galería
          <input
            type="file"
            accept="image/*"
            multiple={varias}
            disabled={disabled}
            onChange={alElegir}
            aria-label={varias === true
              ? `Elegir ${nombre} de la galería (puedes marcar varias)`
              : `Elegir ${nombre} de la galería`}
            className="hidden"
          />
        </label>
      </div>
      {lista && <span className="mt-1 block text-xs text-green-700">{listaTexto}</span>}
      {rechazo !== null && (
        <span role="alert" className="mt-1 block text-xs text-red-600">{rechazo}</span>
      )}
    </div>
  );
}
